import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import ContactFooter from "../components/ContactFooter";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ENTITY_NAME, SUPPORT_EMAIL } from "../data/siteContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "", company: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: "", email: "", message: "", company: "" });
      toast.success("Message received. We will follow up soon.");
    } catch (err) {
      console.error(err, "error submitting contact form");
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-parchment min-h-screen antialiased">
      <Header light />
      <article className="pt-36 sm:pt-44 pb-24">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-goldeep text-sm font-medium tracking-wide hover:text-ink transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>

          <div className="flex items-center gap-3 text-[11px] sm:text-xs tracking-[0.3em] uppercase font-semibold text-gold mt-8">
            <span className="h-px w-10 bg-gold/60" />
            Contact
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-ink tracking-tight leading-[1.1] mt-5">
            Get In <em className="text-goldeep italic">Touch.</em>
          </h1>
          <p className="text-ink/70 text-base sm:text-lg leading-relaxed mt-6">
            {ENTITY_NAME} operates Iron Gate Consulting. Send a message below and we will get
            back to you, or email us directly.
          </p>

          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-2 text-goldeep font-semibold mt-5 hover:text-ink transition-colors"
            data-testid="contact-page-email"
          >
            <Mail className="w-4 h-4" /> {SUPPORT_EMAIL}
          </a>

          <div className="mt-10 bg-white border border-ink/10 rounded-3xl p-8 sm:p-10 shadow-xl">
            {submitted ? (
              <div className="text-center py-6" data-testid="contact-success-message">
                <p className="font-display text-2xl text-ink">Message Received.</p>
                <p className="text-ink/60 mt-3 leading-relaxed">
                  Thank you for reaching out. We have your message on file and will follow up at
                  the email you provided.
                </p>
                <Button
                  variant="outline"
                  className="mt-6"
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another-btn"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {/* Honeypot -- hidden from real visitors, bots often auto-fill it */}
                <div
                  className="absolute -left-[9999px] w-px h-px overflow-hidden"
                  aria-hidden="true"
                >
                  <label htmlFor="company">Company</label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.company}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <Label htmlFor="name" className="text-ink/70 text-sm">
                    Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="mt-2"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-ink/70 text-sm">
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="mt-2"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-ink/70 text-sm">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    rows={5}
                    className="mt-2"
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-ink text-cream hover:bg-goldeep font-bold tracking-[0.08em] uppercase text-sm py-6 rounded-full transition-colors duration-300"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? "Sending..." : "Send Message"}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </form>
            )}
          </div>
        </div>
      </article>
      <ContactFooter />
    </div>
  );
};

export default Contact;
